import React from 'react'
import { Alert, Box, CircularProgress, useTheme } from '@mui/material'

import { MainPageBox } from '../../components/DisplayHelpers/MainPageBox'
import { useAuth } from '../../components/AuthContext/AuthProvider'
import EmailVerificationContainer from '../../components/EmailVerification/EmailVerification.container'
import { useApp } from '../../components/AppContext/AppProvider'
import { CreditApplication } from '../../domain/creditApplication'
import {
  determineInitialState,
  findApplications
} from '../../services/creditApplication.service'
import { ApplicationProgressContainer } from '../../components/ApplicationProgress/ApplicationProgress.container'
import PhoneVerificationContainer from '../../components/PhoneVerification/PhoneVerification.container'
import { PaperWrapper } from '../../components/DisplayHelpers/PaperWrapper'
import { useFetch } from '../../hooks/fetch.hook'

const ApplicationContainer: React.FC = (): JSX.Element => {
  const theme = useTheme()
  const { loading, userCredentials, user } = useAuth()
  const { application } = useApp()
  const isEmailVerified: boolean = userCredentials?.verifiedEmail === true
  const isPhoneVerified: boolean = userCredentials?.verifiedPhone === true

  const {
    data: creditApplications,
    loading: loadingApps,
    error
  } = useFetch<CreditApplication[]>(findApplications(application))

  const RenderLoader: React.FC = () => {
    return (
      <Box
        sx={{
          textAlign: 'center',
          mt: theme.spacing(5)
        }}
      >
        <CircularProgress color="secondary" />
      </Box>
    )
  }

  const RenderError: React.FC = () => {
    return (
      <Box
        sx={{
          textAlign: 'center',
          mt: theme.spacing(5)
        }}
      >
        <Alert severity="error">Something went wrong!</Alert>
      </Box>
    )
  }

  const RenderStep: React.FC = () => {
    if (loadingApps || !creditApplications) {
      return (
        <PaperWrapper>
          <RenderLoader />
        </PaperWrapper>
      )
    }

    if (!isEmailVerified) {
      return (
        <PaperWrapper>
          <EmailVerificationContainer />
        </PaperWrapper>
      )
    }

    if (!isPhoneVerified) {
      return (
        <PaperWrapper>
          <PhoneVerificationContainer />
        </PaperWrapper>
      )
    }

    if (isEmailVerified && isPhoneVerified) {
      return (
        <ApplicationProgressContainer
          creditApplications={creditApplications || []}
          initialState={determineInitialState(user, userCredentials)}
        />
      )
    }

    return <>{!loadingApps && error && <RenderError />}</>
  }

  return (
    <MainPageBox>{loading ? <RenderLoader /> : <RenderStep />}</MainPageBox>
  )
}

export default ApplicationContainer
